import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Form,
  InputGroup,
  Modal,
  Pagination,
  Row,
  Col,
  Table,
} from "react-bootstrap";
import { Formik, Form as FormikForm } from "formik";
import * as Yup from "yup";
import { FaChevronDown, FaChevronUp, FaPlus, FaSearch, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { adminApi } from "../../api/services";
import { getErrorMessage } from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import FormField from "../../components/forms/FormField";
import UserDetailsPanel from "../../components/UserDetailsPanel";
import {
  buildPageItems,
  filterAndSortUsers,
  paginateItems,
  USER_PAGE_SIZES,
  USER_ROLES,
  USER_SORT_OPTIONS,
} from "../../utils/adminUsers";
import { TableSkeleton } from "../../components/Skeleton";
import "./ManagePages.css";

const ROLE_VARIANT = {
  admin: "danger",
  sales: "primary",
  customer: "secondary",
};

const DEFAULT_FILTERS = {
  search: "",
  role: "all",
  status: "all",
  dateFrom: "",
  dateTo: "",
  sortBy: "newest",
};

const createUserSchema = Yup.object({
  name: Yup.string().trim().required("Name is required").min(2).max(120),
  email: Yup.string().trim().email("Enter a valid email").required("Email is required"),
  phone: Yup.string().trim().required("Phone is required").min(7).max(30),
  password: Yup.string().required("Password is required").min(8).max(128),
  role: Yup.string().oneOf(USER_ROLES).required("Role is required"),
});

const emptyCreateForm = {
  name: "",
  email: "",
  phone: "",
  password: "",
  role: "customer",
};

const ManageUsers = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.users();
      setUsers(res.data.data.users || []);
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load users"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredUsers = useMemo(
    () => filterAndSortUsers(users, filters),
    [users, filters]
  );

  const pagination = useMemo(
    () => paginateItems(filteredUsers, page, pageSize),
    [filteredUsers, page, pageSize]
  );

  useEffect(() => {
    if (page > pagination.totalPages) setPage(pagination.totalPages);
  }, [page, pagination.totalPages]);

  const hasActiveFilters =
    filters.search.trim() ||
    filters.role !== "all" ||
    filters.status !== "all" ||
    filters.dateFrom ||
    filters.dateTo ||
    filters.sortBy !== "newest";

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
    setExpandedId(null);
  };

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
    setExpandedId(null);
  };

  const patchUser = async (target, payload) => {
    setSavingId(target.id);
    try {
      const res = await adminApi.updateUser(target.id, payload);
      const updated = res.data.data.user;
      setUsers((prev) => prev.map((u) => (u.id === updated.id ? updated : u)));
      toast.success("User updated");
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not update user"));
    } finally {
      setSavingId(null);
    }
  };

  const handleActiveToggle = (target, nextActive) => {
    if (nextActive) {
      patchUser(target, { isActive: true });
      return;
    }

    const confirmed = window.confirm(
      `Deactivate ${target.name}? They will be logged out and cannot sign in until reactivated.`
    );
    if (confirmed) patchUser(target, { isActive: false });
  };

  const handleCreateUser = async (values, { setSubmitting, resetForm }) => {
    try {
      const res = await adminApi.createUser(values);
      const created = res.data.data.user;
      setUsers((prev) => [created, ...prev]);
      toast.success("User created");
      resetForm();
      setShowCreateModal(false);
    } catch (error) {
      toast.error(getErrorMessage(error, "Could not create user"));
    } finally {
      setSubmitting(false);
    }
  };

  const pageItems = buildPageItems(pagination.page, pagination.totalPages);

  return (
    <div className="manage-page">
      <header className="manage-head">
        <div>
          <h1>Customers &amp; staff</h1>
          <p>Search, filter, and manage user accounts, roles, and access.</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="manage-count">
            {filteredUsers.length === users.length
              ? `${users.length} total`
              : `${filteredUsers.length} of ${users.length}`}
          </span>
          <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
            <FaPlus className="me-1" /> Add user
          </Button>
        </div>
      </header>

      {!loading && users.length > 0 && (
        <div className="manage-toolbar">
          <InputGroup className="manage-search">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="search"
              placeholder="Search name, email, phone, role…"
              value={filters.search}
              onChange={(e) => updateFilter("search", e.target.value)}
            />
          </InputGroup>

          <Form.Select
            className="manage-filter"
            value={filters.role}
            onChange={(e) => updateFilter("role", e.target.value)}
          >
            <option value="all">All roles</option>
            {USER_ROLES.map((role) => (
              <option key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter"
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </Form.Select>

          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateFrom}
            onChange={(e) => updateFilter("dateFrom", e.target.value)}
            aria-label="Joined from"
          />

          <Form.Control
            type="date"
            className="manage-filter manage-date"
            value={filters.dateTo}
            onChange={(e) => updateFilter("dateTo", e.target.value)}
            aria-label="Joined to"
          />

          <Form.Select
            className="manage-filter"
            value={filters.sortBy}
            onChange={(e) => updateFilter("sortBy", e.target.value)}
          >
            {USER_SORT_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="manage-filter manage-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
              setExpandedId(null);
            }}
          >
            {USER_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                Show {size}
              </option>
            ))}
          </Form.Select>

          {hasActiveFilters && (
            <Button
              variant="outline-secondary"
              size="sm"
              className="manage-clear-btn"
              onClick={clearFilters}
            >
              <FaTimes /> Clear
            </Button>
          )}
        </div>
      )}

      {loading ? (
        <TableSkeleton rows={8} cols={5} />
      ) : users.length === 0 ? (
        <div className="manage-empty">
          <p>No users found.</p>
          <Button variant="primary" size="sm" onClick={() => setShowCreateModal(true)}>
            <FaPlus className="me-1" /> Add first user
          </Button>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="manage-empty">
          <p>No users match your search or filters.</p>
          <Button variant="link" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      ) : (
        <>
          <div className="manage-card table-responsive">
            <Table hover className="manage-table align-middle mb-0">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact</th>
                  <th>Joined</th>
                  <th>Role</th>
                  <th>Active</th>
                </tr>
              </thead>
              <tbody>
                {pagination.items.map((u) => {
                  const isSelf = u.id === currentUser?.id;
                  const isExpanded = expandedId === u.id;
                  const isActive = u.isActive !== false;

                  return (
                    <React.Fragment key={u.id}>
                      <tr className={!isActive ? "manage-row-muted" : undefined}>
                        <td>
                          <div className="manage-order-code">
                            <strong>{u.name}</strong>
                            {isSelf && (
                              <Badge bg="warning" text="dark" className="ms-2">
                                You
                              </Badge>
                            )}
                            {!isActive && (
                              <Badge bg="secondary" className="ms-2">
                                Inactive
                              </Badge>
                            )}
                            <Button
                              variant="link"
                              size="sm"
                              className="manage-expand-btn"
                              onClick={() => setExpandedId(isExpanded ? null : u.id)}
                            >
                              {isExpanded ? (
                                <>
                                  <FaChevronUp /> Hide
                                </>
                              ) : (
                                <>
                                  <FaChevronDown /> Details
                                </>
                              )}
                            </Button>
                          </div>
                        </td>
                        <td>
                          <div>{u.email}</div>
                          <div className="manage-muted">{u.phone || "—"}</div>
                        </td>
                        <td>{new Date(u.createdAt).toLocaleDateString()}</td>
                        <td className="text-nowrap">
                          <Badge
                            bg={ROLE_VARIANT[u.role] || "secondary"}
                            className="me-2 text-capitalize"
                          >
                            {u.role}
                          </Badge>
                          <Form.Select
                            size="sm"
                            className="manage-select"
                            value={u.role}
                            disabled={savingId === u.id || isSelf}
                            onChange={(e) => patchUser(u, { role: e.target.value })}
                          >
                            {USER_ROLES.map((r) => (
                              <option key={r} value={r}>
                                {r}
                              </option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Check
                            type="switch"
                            id={`user-active-${u.id}`}
                            checked={isActive}
                            disabled={savingId === u.id || isSelf}
                            onChange={(e) => handleActiveToggle(u, e.target.checked)}
                            label={isActive ? "Active" : "Inactive"}
                          />
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr className="manage-order-details-row">
                          <td colSpan={5}>
                            <UserDetailsPanel user={u} />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </Table>
          </div>

          <div className="manage-pagination-bar">
            <p className="manage-pagination-meta">
              Showing {pagination.showingFrom}–{pagination.showingTo} of {pagination.total}{" "}
              users
            </p>

            {pagination.totalPages > 1 && (
              <Pagination className="manage-pagination mb-0">
                <Pagination.Prev
                  disabled={pagination.page <= 1}
                  onClick={() => {
                    setPage((p) => Math.max(1, p - 1));
                    setExpandedId(null);
                  }}
                />
                {pageItems.map((item, index) =>
                  item === "…" ? (
                    <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={item}
                      active={item === pagination.page}
                      onClick={() => {
                        setPage(item);
                        setExpandedId(null);
                      }}
                    >
                      {item}
                    </Pagination.Item>
                  )
                )}
                <Pagination.Next
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => {
                    setPage((p) => Math.min(pagination.totalPages, p + 1));
                    setExpandedId(null);
                  }}
                />
              </Pagination>
            )}
          </div>
        </>
      )}

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add user</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={emptyCreateForm}
          validationSchema={createUserSchema}
          onSubmit={handleCreateUser}
        >
          {({ isSubmitting }) => (
            <FormikForm noValidate>
              <Modal.Body>
                <Row className="g-3">
                  <Col md={12}>
                    <FormField name="name" label="Full name" placeholder="Jane Doe" />
                  </Col>
                  <Col md={6}>
                    <FormField
                      name="email"
                      type="email"
                      label="Email"
                      placeholder="user@example.com"
                    />
                  </Col>
                  <Col md={6}>
                    <FormField name="phone" label="Phone" placeholder="+971..." />
                  </Col>
                  <Col md={6}>
                    <FormField
                      name="password"
                      type="password"
                      label="Temporary password"
                      placeholder="Min. 8 characters"
                    />
                  </Col>
                  <Col md={6}>
                    <FormField name="role" label="Role">
                      {USER_ROLES.map((role) => (
                        <option key={role} value={role}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </option>
                      ))}
                    </FormField>
                  </Col>
                </Row>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={isSubmitting}>
                  {isSubmitting ? "Creating…" : "Create user"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default ManageUsers;
